/**
 * data/platformSeed.ts
 * Seed/fallback data ported from PLATFROM_DESIGN_INSTRUCTIONS/design/data.jsx.
 * Screens render from live API responses where available; this provides the
 * initial shape + an offline fallback so the UI is fully navigable in dev
 * before every backend route is live. Shapes mirror the design exactly.
 */

export type Role = 'ADMIN' | 'EDITOR' | 'VIEWER';
export const ROLES: Role[] = ['ADMIN', 'EDITOR', 'VIEWER'];

export interface Member {
  id: string; name: string; email: string; role: Role; lastSeen: string;
  status: 'active' | 'deactivated';
  /**
   * CH-U2 — the employment window. skplatform-api keeps one membership row per
   * window (CC-002 CH-3), so a former member still has a record and the console
   * has to be able to show `joined … → deactivated …`. Optional because a row
   * from an older API response may not carry them yet.
   */
  joinedAt?: string;
  deactivatedAt?: string;
}
export interface Invitation {
  id: string; email: string; role: Role; invited: string; expiresInDays: number;
}

export const SEED = {
  org: {
    name: 'Acme Retail Pvt Ltd',
    address:
      '4th Floor, Prestige Tech Park\nMarathahalli, Bengaluru 560103\nKarnataka, India',
    gstin: '29ABCDE1234F1Z5',
    country: 'India',
    logo: null as string | null,
  },
  currentUser: { name: 'Anjali Verma', email: 'anjali@acmeretail.in' },
  members: [
    { id: 'u1', name: 'Anjali Verma', email: 'anjali@acmeretail.in', role: 'ADMIN', lastSeen: 'Just now', status: 'active', joinedAt: '2024-02-05T09:00:00.000Z' },
    { id: 'u2', name: 'Raj Iyer', email: 'raj@acmeretail.in', role: 'EDITOR', lastSeen: '12m ago', status: 'active', joinedAt: '2024-06-18T09:00:00.000Z' },
    { id: 'u3', name: 'Sara Mathew', email: 'sara@acmeretail.in', role: 'EDITOR', lastSeen: '2h ago', status: 'active', joinedAt: '2025-03-12T09:00:00.000Z' },
    { id: 'u4', name: 'Kabir Nair', email: 'kabir@acmeretail.in', role: 'VIEWER', lastSeen: 'Yesterday', status: 'active', joinedAt: '2025-11-02T09:00:00.000Z' },
    { id: 'u5', name: 'Meera Pillai', email: 'meera@acmeretail.in', role: 'VIEWER', lastSeen: '3 days ago', status: 'deactivated', joinedAt: '2024-09-01T09:00:00.000Z', deactivatedAt: '2026-08-20T11:30:00.000Z' },
  ] as Member[],
  invitations: [
    { id: 'i1', email: 'dev@acmeretail.in', role: 'EDITOR', invited: '2 days ago', expiresInDays: 5 },
    { id: 'i2', email: 'finance@acmeretail.in', role: 'VIEWER', invited: '6 days ago', expiresInDays: 1 },
  ] as Invitation[],
  usage: [
    { label: 'Active contacts', used: 2180, cap: 2500, unit: '' },
    { label: 'Team seats', used: 5, cap: 10, unit: '' },
    { label: 'Custom fields', used: 24, cap: 25, unit: '' },
    { label: 'Suki Voice min', used: 470, cap: 500, unit: 'min' },
  ],
  audit: [
    { id: 'evt_9f2a4c', ts: '2026-06-07 09:14:02', actor: 'Anjali Verma', cat: 'login', detail: 'Signed in from Bengaluru, IN (Chrome)' },
    { id: 'evt_8b1d77', ts: '2026-06-07 08:51:30', actor: 'Anjali Verma', cat: 'plan', detail: 'Viewed plan & usage' },
    { id: 'evt_7c0e21', ts: '2026-06-06 17:22:10', actor: 'Raj Iyer', cat: 'invitation', detail: 'Invited dev@acmeretail.in as EDITOR' },
    { id: 'evt_6a9f03', ts: '2026-06-06 14:08:44', actor: 'Anjali Verma', cat: 'deletion', detail: 'Erased contact #C-1042 (DPDP)' },
    { id: 'evt_5e7b18', ts: '2026-06-05 11:30:55', actor: 'Sara Mathew', cat: 'login', detail: 'Signed in from Kochi, IN (Safari)' },
    { id: 'evt_4d6c90', ts: '2026-06-04 19:45:12', actor: 'Anjali Verma', cat: 'plan', detail: 'Changed plan Starter → Standard' },
    { id: 'evt_3f5a2b', ts: '2026-06-04 10:02:39', actor: 'Kabir Nair', cat: 'login', detail: 'Signed in from Bengaluru, IN (Edge)' },
    { id: 'evt_2b4d81', ts: '2026-06-03 16:19:07', actor: 'Anjali Verma', cat: 'invitation', detail: 'Invited finance@acmeretail.in as VIEWER' },
  ],
  sessions: [
    { id: 's1', device: 'Chrome · macOS', loc: 'Bengaluru, IN', current: true, lastActive: 'Active now' },
    { id: 's2', device: 'Safari · iPhone', loc: 'Bengaluru, IN', current: false, lastActive: '5h ago' },
    { id: 's3', device: 'Edge · Windows', loc: 'Mumbai, IN', current: false, lastActive: '2 days ago' },
  ],
  contacts: [
    { id: 'C-1088', name: 'Rohan Desai', email: 'rohan.d@example.in', phone: '+91 98200 11223' },
    { id: 'C-1072', name: 'Priya Sharma', email: 'priya.s@example.in', phone: '+91 99300 44556' },
    { id: 'C-1051', name: 'Imran Sheikh', email: 'imran.s@example.in', phone: '+91 90040 77889' },
    { id: 'C-1033', name: 'Lakshmi Rao', email: 'lakshmi.r@example.in', phone: '+91 98860 33221' },
  ],

  /* PLATFORM ADMIN */
  applications: [
    { id: 'app_crm', name: 'SK CRM', key: 'crm', icon: 'users', category: 'Core', status: 'active', desc: 'Contacts, pipelines, bookings & customer records.', version: '4.2.0', tenants: 312 },
    { id: 'app_catalog', name: 'SK Catalog', key: 'catalog', icon: 'star', category: 'Core', status: 'active', desc: 'Products, services & inventory catalog.', version: '2.8.1', tenants: 287 },
    { id: 'app_voice', name: 'Suki Voice', key: 'voice', icon: 'phone', category: 'Add-on', status: 'beta', desc: 'AI call routing, transcription & recordings.', version: '0.9.4', tenants: 64 },
    { id: 'app_inbox', name: 'SK Inbox', key: 'inbox', icon: 'mail', category: 'Add-on', status: 'active', desc: 'Shared team inbox across email, chat & WhatsApp.', version: '1.5.0', tenants: 198 },
    { id: 'app_insight', name: 'SK Insights', key: 'insights', icon: 'eye', category: 'Add-on', status: 'deprecated', desc: 'Legacy reporting suite — superseded by Analytics.', version: '1.0.7', tenants: 12 },
  ],
  tenants: [
    { id: 't1', name: 'Acme Retail Pvt Ltd', plan: 'Standard', seats: 5, status: 'active', signup: '12 Feb 2026', mrr: 1499, zone: 'in' },
    { id: 't2', name: 'Bluebird Logistics', plan: 'Growth', seats: 22, status: 'active', signup: '03 Jan 2026', mrr: 3999, zone: 'in' },
    { id: 't3', name: 'Coastal Foods Co', plan: 'Starter', seats: 3, status: 'trial', signup: '29 May 2026', mrr: 0, zone: 'in' },
    { id: 't4', name: 'Devi Textiles', plan: 'Scale', seats: 71, status: 'active', signup: '19 Nov 2025', mrr: 9999, zone: 'in' },
    { id: 't5', name: 'Evergreen Pharma', plan: 'Standard', seats: 8, status: 'past_due', signup: '08 Mar 2026', mrr: 1499, zone: 'in' },
    { id: 't6', name: 'Frontier Motors', plan: 'Growth', seats: 18, status: 'suspended', signup: '22 Dec 2025', mrr: 0, zone: 'ae' },
  ],

  /* PRODUCT SURFACES */
  crmContacts: [
    { id: 'C-1088', name: 'Rohan Desai', company: 'Desai Traders', email: 'rohan.d@example.in', phone: '+91 98200 11223', stage: 'Lead', owner: 'Raj Iyer', value: 45000 },
    { id: 'C-1072', name: 'Priya Sharma', company: 'Sharma & Co', email: 'priya.s@example.in', phone: '+91 99300 44556', stage: 'Qualified', owner: 'Anjali Verma', value: 120000 },
    { id: 'C-1063', name: 'Imran Sheikh', company: 'Sheikh Exports', email: 'imran.s@example.in', phone: '+91 90040 77889', stage: 'Proposal', owner: 'Sara Mathew', value: 210000 },
    { id: 'C-1051', name: 'Lakshmi Rao', company: 'Rao Industries', email: 'lakshmi.r@example.in', phone: '+91 98860 33221', stage: 'Won', owner: 'Anjali Verma', value: 340000 },
    { id: 'C-1042', name: 'Vikram Nair', company: 'Nair Logistics', email: 'vikram.n@example.in', phone: '+91 97410 88990', stage: 'Lead', owner: 'Raj Iyer', value: 28000 },
    { id: 'C-1037', name: 'Fatima Khan', company: 'Khan Retail', email: 'fatima.k@example.in', phone: '+91 90080 22114', stage: 'Qualified', owner: 'Sara Mathew', value: 95000 },
    { id: 'C-1029', name: 'Arjun Menon', company: 'Menon Foods', email: 'arjun.m@example.in', phone: '+91 98765 43210', stage: 'Proposal', owner: 'Raj Iyer', value: 175000 },
    { id: 'C-1014', name: 'Deepa Pillai', company: 'Pillai Textiles', email: 'deepa.p@example.in', phone: '+91 93210 55667', stage: 'Won', owner: 'Anjali Verma', value: 410000 },
  ],
  catalogItems: [
    { id: 'SKU-204', name: 'Cold-Pressed Coconut Oil 1L', type: 'Product', price: 480, stock: 320, status: 'active' },
    { id: 'SKU-198', name: 'Organic Basmati Rice 5kg', type: 'Product', price: 720, stock: 145, status: 'active' },
    { id: 'SKU-187', name: 'Filter Coffee Blend 500g', type: 'Product', price: 350, stock: 0, status: 'active' },
    { id: 'SKU-156', name: 'Spice Box Gift Set', type: 'Product', price: 1250, stock: 58, status: 'draft' },
    { id: 'SVC-031', name: 'Same-day Delivery (per order)', type: 'Service', price: 60, stock: null, status: 'active' },
    { id: 'SVC-022', name: 'Gift Wrapping', type: 'Service', price: 40, stock: null, status: 'active' },
    { id: 'SKU-141', name: 'Stainless Steel Tiffin 3-tier', type: 'Product', price: 890, stock: 27, status: 'archived' },
    { id: 'SKU-133', name: 'Jaggery Powder 1kg', type: 'Product', price: 180, stock: 210, status: 'active' },
  ],
  voiceCalls: [
    { id: 'CL-9921', contact: 'Rohan Desai', direction: 'inbound', duration: 214, status: 'completed', agent: 'Suki AI', time: 'Today · 09:12', summary: 'Asked about bulk coconut oil pricing; routed to Raj.' },
    { id: 'CL-9918', contact: 'Priya Sharma', direction: 'outbound', duration: 92, status: 'completed', agent: 'Anjali Verma', time: 'Today · 08:40', summary: 'Follow-up on proposal; sent revised quote.' },
    { id: 'CL-9910', contact: 'Unknown', direction: 'inbound', duration: 0, status: 'missed', agent: '—', time: 'Today · 08:05', summary: 'Missed call, no voicemail.' },
    { id: 'CL-9902', contact: 'Imran Sheikh', direction: 'inbound', duration: 367, status: 'completed', agent: 'Suki AI', time: 'Yesterday · 17:22', summary: 'Export shipment query; booking created.' },
    { id: 'CL-9897', contact: 'Fatima Khan', direction: 'outbound', duration: 145, status: 'completed', agent: 'Sara Mathew', time: 'Yesterday · 15:08', summary: 'Onboarding walkthrough for new retail account.' },
    { id: 'CL-9890', contact: 'Vikram Nair', direction: 'inbound', duration: 58, status: 'voicemail', agent: 'Suki AI', time: 'Yesterday · 11:47', summary: 'Left voicemail about delivery delay.' },
  ],
};

export type PlatformState = typeof SEED;

export function freshState(): PlatformState {
  return JSON.parse(JSON.stringify(SEED));
}
